import React, { lazy, useEffect, useRef, useState } from "react";

/** Common Components */
import CardSection from "../../components/ui/card/CardSection";
import CenterModal from "../../components/ui/centerModal/CenterModal";

/** Master Valuation Fields Page Styling */
import "./MasterValuationFields.scss";
import DropdownSelect from "../../components/forms/ui/inputs/dropdown/DropdownSelect";
import Label from "../../components/forms/ui/label/Label";
import {
  useGetAllMasterFieldCategoriesMutation,
  useGetAllOriginMutation,
} from "../../app/services/MasterValuationFieldsAPI";
import SearchBar from "../../components/ui/searchBar/SearchBar";
import useDebounce from "../../app/customHooks/useDebounce";

/** Lazily Loaded Components */
const MasterValuationFieldsList = lazy(() =>
  import("./components/masterValuationFieldsList/MasterValuationFieldsList")
);
const AddEditMasterValuationField = lazy(() =>
  import("./components/addEditMasterValuationField/AddEditMasterValuationField")
);

const MasterValuationFields = () => {
  const molGridRef = useRef();
  const [isEdit, setIsEdit] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [isUserModelOpen, setIsUserModelOpen] = useState(false);
  const [selectedOriginType, setSelectedOriginType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300);

  const [
    getAllMasterFieldCategories,
    {
      isLoading: isAllMasterFieldCategoriesListLoading,
      isSuccess: isAllMasterFieldCategoriesListSuccess,
      data: allMasterFieldCategoriesData,
    },
  ] = useGetAllMasterFieldCategoriesMutation();

  const [
    getAllOrigin,
    {
      isLoading: isAllOriginLoading,
      isSuccess: isAllOriginSuccess,
      data: allOriginData,
    },
  ] = useGetAllOriginMutation();

  useEffect(() => {
    getAllMasterFieldCategories();
    getAllOrigin();
  }, []);

  useEffect(() => {
    if (isAllMasterFieldCategoriesListSuccess && allMasterFieldCategoriesData) {
      let allCategorie = {
        label: "All Categories",
        id: "All Categories",
      };
      let categoryOptions = allMasterFieldCategoriesData.map((item) => {
        return {
          label: item.masterFieldCategory,
          value: item.id,
        };
      });
      const updatedCategories = Array.from(
        new Set([allCategorie, ...categoryOptions])
      );
      setSelectedCategory(updatedCategories[0])
      setCategoryOptions(updatedCategories);
    }
  }, [isAllMasterFieldCategoriesListSuccess, allMasterFieldCategoriesData]);

  useEffect(() => {
    if (isAllOriginSuccess && allOriginData) {
      let allOrgin = {
        label: "All Origin",
        id: 0,
      };
      let originOptions = allOriginData.map((item) => {
        return {
          label: item.originType,
          value: item.originId,
        };
      });
      // const updatedOrigin = Array.from(new Set([allOrgin, ...originOptions]));
      const updatedOrigin = [allOrgin, ...originOptions].sort((a, b) =>
      a?.label.localeCompare(b?.label)
    );
      setSelectedOriginType(updatedOrigin[0])
      setOriginOptions(updatedOrigin);
    }
  }, [isAllOriginSuccess, allOriginData]);

  const handleAddUserClick = () => {
    setIsUserModelOpen(true);
    setEditUserData(null);
    setIsEdit(false);
  };

  const handleCloseModel = () => {
    setIsUserModelOpen(false);
    setEditUserData(null);
    setIsEdit(false);
  };

  const handleEditClick = (data) => {
    setIsUserModelOpen(true);
    setEditUserData(data);
    setIsEdit(true);
  };

  const handleSuccess = () => {
    if (molGridRef.current) {
      molGridRef.current?.refreshGrid();
      handleCloseModel();
    }
  };

  const handleOriginTypeChange = (selected) => {
    setSelectedOriginType(selected);
  };

  const handleCategoryChange = (selected) => {
    setSelectedCategory(selected);
  };

  const handleSerachName = (e) => {
    setSearchValue(e?.target?.value);
  };

  return (
    <>
      <div className="master-valuation-fields">
        <CardSection cardTitle={"Filter Options"}>
          <div className="row">
            <div className="col-4">
              <Label labelName={"Name"} />
              <SearchBar 
              placeholder = {"Search by Name"}
              onChange={handleSerachName} />
            </div>
            <div className="col-4">
              <Label labelName={"Origin Type"} />
              <DropdownSelect
                optionsValue={originOptions}
                placeholder={"Select Origin Type"}
                isMultiSelect={false}
                isDropdownDisabled={false}
                handleDropdownChange={handleOriginTypeChange}
                value={selectedOriginType}
              />
            </div>
            <div className="col-4">
              <Label labelName={"Category"} />
              <DropdownSelect
                optionsValue={categoryOptions}
                placeholder={"Select Category"}
                isMultiSelect={false}
                isDropdownDisabled={false}
                handleDropdownChange={handleCategoryChange}
                value={selectedCategory}
              />
            </div>
          </div>
        </CardSection>
        <div className="second-section">
          <CardSection
            cardTitle={"Master Valuation Fields"}
            rightButton={true}
            buttonClassName={"blue-btn"}
            titleButtonClick={handleAddUserClick}
            buttonText={"ADD"}
            searchInput={false}
            isButtonVisible={true}
            isButtonLoading={""}
            isIcon={true}
            iconClass="uil:plus"
          >
            <div className="master-valuation-body-scroll">
              <MasterValuationFieldsList
                ref={molGridRef}
                onEdit={handleEditClick}
                selectedCategory={selectedCategory}
                selectedOriginType={selectedOriginType}
                search={debouncedSearch}
                // onSuccess={handleSuccess}
              />
            </div>
          </CardSection>
        </div>
      </div>

      <CenterModal
        modalTitle={
          isEdit
            ? `Update Master Valuation Field`
            : `Add Master Valuation Field`
        }
        modalSize={"w-50"}
        isOpen={isUserModelOpen}
        onClose={handleCloseModel}
        transition={"grow"}
      >
        <AddEditMasterValuationField
          isEdit={isEdit}
          isOpen={isUserModelOpen}
          initData={editUserData}
          onClose={handleCloseModel}
          onSuccess={handleSuccess}
        />
      </CenterModal>
    </>
  );
};

export default MasterValuationFields;
